import React, { useState } from "react";
import Input from "components/Input/index";
import Select from "components/Select/index";
import SwitchInput from "components/Toggles/Switch/index.jsx";
import DateInput from "components/CustomDateModal/DateInput";
import RadioLabelComponent from "components/Toggles/RadioLabelComponent";
import MultiSelectSearch from "components/MultiSelectSearch/index";
import { Label } from "components/Input/styled";
import { EditCreatorFormWrapperStyles } from "../styled";
import { creatorTypeOptions } from "pages/Creators/data";
import { validateEmail, validatePhone } from "utils/helper";

export const CreatorInfoSection = ({
  form,
  setForm,
  // Handlers
  handleChange,
  handleStateChange,
  handleCityChange,
  handlePrimaryCategoryChange,
  handleSubCategoryChange,
  handlePrimaryCategorySearch,
  handleSubCategorySearch,
  handleLanguageSearch,
  handleTalentCompanyChange,
  // Dropdown items
  statesMenuItems,
  citiesMenuItems,
  languageMenuItems,
  primaryCategoriesMenuItems,
  subCategoriesMenuItems,
  companiesMenuItems,
  talentAgencyManagerMenuItems,
  managedByMenuItems,
  // Loading
  citiesLoading,
  languageLoading,
  primaryCategoriesLoading,
  subCategoriesLoading,
  talentAgencyManagersLoading,
  // Dropdown raw data (for dispatch)
  dispatch,
  dropdownActions,

  phoneError,
  setPhoneError,
  emailError,
  setEmailError,
  managerPhoneError,
  setManagerPhoneError,
  managerEmailError,
  setManagerEmailError
}) => {
  return (
    <EditCreatorFormWrapperStyles>
      <div className="form-header-wrapper">
        <h2 className="title">Creator Info</h2>
      </div>

      <div className="form-content-wrapper">
        <div className="row-group">
          {/* ── Left column ── */}
          <div className="column-group">
            <Input
              id="name"
              label="Name"
              value={form.name || ""}
              onChange={handleChange}
              size="M"
            />

            <Input
              countryCodeOnlyDisabled
              id="phone"
              codeId="phone_id"
              placeholder="Enter Phone Number"
              countryCodePlaceholder="+91"
              type="tel"
              label="Phone Number"
              maxLength={10}
              value={form.phone || ""}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);

                setForm((prev) => ({
                  ...prev,
                  phone: value
                }));

                // Only validate if user typed something
                if (value.length > 0) {
                  setPhoneError(validatePhone(value));
                } else {
                  setPhoneError("");
                }
              }}
              hasError={!!phoneError}
              helperText={phoneError}
              required={!form.managed_by || form.managed_by === "self_managed"}
            />

            <Input
              id="email"
              label="Email"
              placeholder="Enter a email"
              value={form.email || ""}
              onChange={(e) => {
                handleChange(e);
                setEmailError(validateEmail(e.target.value));
              }}
              size="M"
              hasError={!!emailError}
              helperText={emailError}
            />

            <div className="radio-input-wrapper">
              <Label as="p" size="M">
                Gender
              </Label>
              <div className="radio-input-group">
                <label className="radio-input-item">
                  <RadioLabelComponent
                    labelHeader="Male"
                    checked={form.gender === "male"}
                    onChange={() =>
                      setForm((prev) => ({ ...prev, gender: "male" }))
                    }
                  />
                  <RadioLabelComponent
                    labelHeader="Female"
                    checked={form.gender === "female"}
                    onChange={() =>
                      setForm((prev) => ({ ...prev, gender: "female" }))
                    }
                  />
                </label>
              </div>
            </div>

            <DateInput
              id="date_of_birth"
              placeholder="Select D.O.B."
              label="D.O.B."
              value={form.date_of_birth || null}
              onChange={(val) =>
                setForm((prev) => ({ ...prev, date_of_birth: val }))
              }
            />

            <Select
              id="state"
              label="State"
              value={form.state_id || ""}
              onChange={handleStateChange}
              placeholder="Select State"
              options={statesMenuItems}
            />

            <Select
              id="city"
              label="City"
              options={citiesMenuItems}
              value={form.city_id || ""}
              onChange={handleCityChange}
              placeholder="Select City"
              loading={citiesLoading}
            />
          </div>

          {/* ── Right column ── */}
          <div className="column-group">
            <MultiSelectSearch
              id="category"
              label="Category (Choose up to 2)"
              options={primaryCategoriesMenuItems}
              value={form.primary_categories || []}
              onChange={handlePrimaryCategoryChange}
              maxSelect={2}
              onSearch={handlePrimaryCategorySearch}
              loading={primaryCategoriesLoading}
            />

            <MultiSelectSearch
              id="sub_category"
              label="Sub-Category (max 2 per category)"
              options={subCategoriesMenuItems}
              value={form.sub_categories || []}
              onChange={handleSubCategoryChange}
              maxSelect={(form.primary_categories?.length || 0) * 2}
              onSearch={handleSubCategorySearch}
              loading={subCategoriesLoading}
              disabled={
                !form.primary_categories?.length || subCategoriesLoading
              }
            />

            <Input
              id="full_time_profession"
              label="Full Time Profession"
              placeholder="Enter Full Time Profession"
              value={form.full_time_profession || ""}
              onChange={handleChange}
              size="M"
            />

            <Select
              id="type"
              label="Creator Type"
              value={form.type || ""}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, type: value }))
              }
              placeholder="Select Creator Type"
              options={creatorTypeOptions}
            />

            <MultiSelectSearch
              id="languages"
              label="Languages"
              options={languageMenuItems}
              value={form.languages || []}
              onChange={(val) =>
                setForm((prev) => ({ ...prev, languages: val }))
              }
              maxSelect={5}
              placeholder="Select Languages"
              onSearch={handleLanguageSearch}
              loading={languageLoading}
            />

            <div className="managed-by-wrapper">
              <Select
                id="managed_by"
                label="Managed by"
                value={form.managed_by || ""}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, managed_by: value }))
                }
                placeholder="Select Type"
                options={managedByMenuItems}
              />
            </div>

            <ManagedByFields
              form={form}
              setForm={setForm}
              companiesMenuItems={companiesMenuItems}
              talentAgencyManagerMenuItems={talentAgencyManagerMenuItems}
              talentAgencyManagersLoading={talentAgencyManagersLoading}
              handleTalentCompanyChange={handleTalentCompanyChange}
              managerEmailError={managerEmailError}
              setManagerEmailError={setManagerEmailError}
              setManagerPhoneError={setManagerPhoneError}
              managerPhoneError={managerPhoneError}
            />
          </div>
        </div>
      </div>
    </EditCreatorFormWrapperStyles>
  );
};

// ─── Managed-by conditional fields ───────────────────────────────────────────

const ManagedByFields = ({
  form,
  setForm,
  companiesMenuItems,
  talentAgencyManagerMenuItems,
  talentAgencyManagersLoading,
  handleTalentCompanyChange,
  managerEmailError,
  setManagerEmailError,
  setManagerPhoneError,
  managerPhoneError
}) => {
  if (form.managed_by === "talent_managed") {
    return (
      <>
        <Select
          id="talent_company_id"
          label="Talent Company Name"
          value={form.talent_company_id || ""}
          onChange={handleTalentCompanyChange}
          placeholder="Select Company"
          options={companiesMenuItems}
          required
        />
        <Select
          id="talent_manager_id"
          label="Talent Agency Manager"
          placeholder="Select Agency Manager"
          value={form.talent_manager_id ?? ""}
          onChange={(value) =>
            setForm((prev) => ({ ...prev, talent_manager_id: value }))
          }
          options={talentAgencyManagerMenuItems}
          loading={talentAgencyManagersLoading}
          required
          disabled={!form.talent_company_id || talentAgencyManagersLoading}
        />
        <SwitchInput
          labelText="Talent Manager Verified"
          id="talent_manager_verified"
          checked={form.is_talent_manager_verified === true}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              is_talent_manager_verified: e.target.checked
            }))
          }
        />
      </>
    );
  }

  if (form.managed_by === "self_managed") {
    return (
      <SwitchInput
        labelText="Self Managed Verified"
        id="self_managed_verified"
        checked={form.is_self_managed_verified}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            is_self_managed_verified: e.target.checked
          }))
        }
      />
    );
  }

  if (form.managed_by === "personal_managed") {
    return (
      <>
        <Input
          id="personal_manager_name"
          label="Personal Manager Name"
          placeholder="Enter Manager Name"
          value={form.personal_manager_name ?? ""}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              personal_manager_name: e.target.value
            }))
          }
          size="M"
          required
        />
        <Input
          id="personal_manager_phone"
          type="tel"
          countryCodeOnlyDisabled
          label="Personal Manager Phone"
          maxLength={10}
          placeholder="Enter Phone Number"
          value={form.personal_manager_phone ?? ""}
          onChange={(e) => {
            if (e.target.value.length > 10) return;
            setForm((prev) => ({
              ...prev,
              personal_manager_phone: e.target.value
            }));
            setManagerPhoneError(validatePhone(e.target.value));
          }}
          size="M"
          codeId="phone_id"
          countryCodePlaceholder="+91"
          country_code_value={form.phone_id || ""}
          onCountryCodeChange={(val) =>
            setForm((prev) => ({ ...prev, phone_id: val }))
          }
          required
          hasError={!!managerPhoneError}
          helperText={managerPhoneError}
        />
        <Input
          id="personal_manager_email"
          label="Personal Manager Email"
          placeholder="Enter Email"
          value={form.personal_manager_email ?? ""}
          onChange={(e) => {
            setForm((prev) => ({
              ...prev,
              personal_manager_email: e.target.value
            }));
            setManagerEmailError(validateEmail(e.target.value));
          }}
          size="M"
          hasError={!!managerEmailError}
          helperText={managerEmailError}
        />
        <SwitchInput
          labelText="Personal Manager Verified"
          id="personal_manager_verified"
          checked={form.is_personal_manager_verified}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              is_personal_manager_verified: e.target.checked
            }))
          }
        />
      </>
    );
  }

  return null;
};
